import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/apiClient';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Plus, Edit, Trash2, Play, Power, PowerOff, Search, Download } from 'lucide-react';
import { toast } from 'sonner';

export function ScraperSitesManager() {
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingSite, setEditingSite] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [logoFile, setLogoFile] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        url: '',
        scrape_interval_hours: 24,
        is_active: true,
        is_hidden: false,
        description: ''
    });

    const queryClient = useQueryClient();

    // Fetch all sites
    const { data: sitesData, isLoading } = useQuery({
        queryKey: ['scraper-sites'],
        queryFn: async () => {
            const response = await api.get('/scraper-sites');
            return response.data;
        },
        refetchInterval: 5000
    });

    // Create site mutation
    const createSiteMutation = useMutation({
        mutationFn: async (data) => {
            // data is FormData or JSON
            const response = await api.post('/scraper-sites', data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['scraper-sites'] });
            resetForm();
            toast.success("Site ajouté avec succès");
        },
        onError: (error) => {
            toast.error(error.response?.data?.error || "Erreur lors de l'ajout");
        }
    });

    // Update site mutation
    const updateSiteMutation = useMutation({
        mutationFn: async ({ id, ...site }) => {
            const response = await api.put(`/scraper-sites/${id}`, site);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['scraper-sites'] });
            resetForm();
        }
    });

    // Delete site mutation
    const deleteSiteMutation = useMutation({
        mutationFn: async (id) => {
            const response = await api.delete(`/scraper-sites/${id}`);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['scraper-sites'] });
        }
    });

    // Trigger scrape mutation
    const scrapeMutation = useMutation({
        mutationFn: async (id) => {
            const response = await api.post(`/scraper-sites/${id}/scrape-now`);
            return response.data;
        }
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        if (editingSite) {
            updateSiteMutation.mutate({ id: editingSite.id, ...formData });
        } else {
            // Validation for logo
            if (logoFile) {
                if (logoFile.size > 2 * 1024 * 1024) {
                    toast.error("L'image ne doit pas dépasser 2 Mo.");
                    return;
                }
                const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
                if (!allowedTypes.includes(logoFile.type)) {
                    toast.error("Format non autorisé. Utilisez JPEG, PNG ou WEBP.");
                    return;
                }
            }

            const data = new FormData();
            data.append('name', formData.name);
            data.append('url', formData.url);
            data.append('scrape_interval_hours', formData.scrape_interval_hours);
            data.append('is_active', formData.is_active);
            data.append('is_hidden', formData.is_hidden);
            data.append('description', formData.description);
            if (logoFile) {
                data.append('logo', logoFile);
            }

            createSiteMutation.mutate(data);
        }
    };

    const resetForm = () => {
        setFormData({ name: '', url: '', scrape_interval_hours: 24, is_active: true, is_hidden: false, description: '' });
        setLogoFile(null);
        setShowAddForm(false);
        setEditingSite(null);
    };

    const startEdit = (site) => {
        setEditingSite(site);
        setFormData({
            name: site.name,
            url: site.url,
            scrape_interval_hours: site.scrape_interval_hours,
            is_active: site.is_active,
            is_hidden: site.is_hidden || false,
            description: site.description || ''
        });
        setShowAddForm(true);
    };

    const getNextScrapeTime = (site) => {
        if (!site.last_scraped_at) return 'Jamais scrappé';
        const lastScraped = new Date(site.last_scraped_at);
        const nextScrape = new Date(lastScraped.getTime() + site.scrape_interval_hours * 60 * 60 * 1000);
        if (nextScrape > new Date()) {
            return `Dans ${Math.ceil((nextScrape - new Date()) / (60 * 60 * 1000))}h`;
        }
        return 'Dû maintenant';
    };

    const filteredSites = sitesData?.sites?.filter(site =>
        site.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        site.url.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const exportToMarkdown = () => {
        if (!sitesData?.sites) {
            toast.error("Aucune donnée à exporter");
            return;
        }

        let mdContent = "# Liste des Marques et URLs\n\n";
        sitesData.sites.forEach(site => {
            mdContent += `- **${site.name}**: ${site.url}\n`;
        });

        const blob = new Blob([mdContent], { type: 'text/markdown' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `marques_${new Date().toISOString().split('T')[0]}.md`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success("Fichier Markdown exporté avec succès");
    };

    return (
        <div className="container mx-auto p-6 space-y-6">
            <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                    <h1 className="text-3xl font-bold">Gestion des Sites à Scraper</h1>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={exportToMarkdown}>
                            <Download className="w-4 h-4 mr-2" />
                            Exporter MD
                        </Button>
                        <Button onClick={() => setShowAddForm(!showAddForm)}>
                            <Plus className="w-4 h-4 mr-2" />
                            Ajouter un site
                        </Button>
                    </div>
                </div>

                <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                        type="search"
                        placeholder="Rechercher un site par nom ou URL..."
                        className="pl-8 max-w-sm"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
            </div>

            {/* Add/Edit Form */}
            {showAddForm && (
                <Card>
                    <CardHeader>
                        <CardTitle>{editingSite ? 'Modifier le site' : 'Ajouter un nouveau site'}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Nom</label>
                                <Input
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="Ma Marque Préférée"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">URL</label>
                                <Input
                                    type="url"
                                    value={formData.url}
                                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                                    placeholder="https://exemple.com/collections/all"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Intervalle de scraping (heures)</label>
                                <Input
                                    type="number"
                                    value={formData.scrape_interval_hours}
                                    onChange={(e) => setFormData({ ...formData, scrape_interval_hours: parseInt(e.target.value) })}
                                    min="1"
                                    required
                                />
                            </div>
                            {!editingSite && (
                                <div>
                                    <label className="block text-sm font-medium mb-1">Logo de la Marque (Max 2 Mo)</label>
                                    <Input
                                        type="file"
                                        accept="image/png, image/jpeg, image/webp"
                                        onChange={(e) => setLogoFile(e.target.files[0])}
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">Sera utilisé pour créer la page Marque associée.</p>
                                </div>
                            )}
                            <div>
                                <label className="block text-sm font-medium mb-1">Description de la marque</label>
                                <textarea
                                    className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="Une marque de streetwear indépendante..."
                                />
                            </div>
                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id="is_active"
                                        checked={formData.is_active}
                                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                    />
                                    <label htmlFor="is_active" className="text-sm">Actif</label>
                                </div>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id="is_hidden"
                                        checked={formData.is_hidden}
                                        onChange={(e) => setFormData({ ...formData, is_hidden: e.target.checked })}
                                    />
                                    <label htmlFor="is_hidden" className="text-sm text-red-500 font-semibold">Invisibiliser (Cacher)</label>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <Button type="submit">
                                    {editingSite ? 'Mettre à jour' : 'Ajouter'}
                                </Button>
                                <Button type="button" variant="outline" onClick={resetForm}>
                                    Annuler
                                </Button>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            )}

            {/* Sites List */}
            <div className="grid gap-4">
                {isLoading ? (
                    <p className="text-center py-8 text-muted-foreground">Chargement...</p>
                ) : filteredSites?.length === 0 ? (
                    <p className="text-center py-8 text-muted-foreground">
                        {searchQuery ? 'Aucun résultat trouvé' : 'Aucun site configuré'}
                    </p>
                ) : (
                    filteredSites?.map((site) => (
                        <Card key={site.id}>
                            <CardContent className="p-6">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                            <h3 className="text-lg font-semibold">{site.name}</h3>
                                            <Badge variant={site.is_active ? 'default' : 'secondary'}>
                                                {site.is_active ? (
                                                    <><Power className="w-3 h-3 mr-1" /> Actif</>
                                                ) : (
                                                    <><PowerOff className="w-3 h-3 mr-1" /> Inactif</>
                                                )}
                                            </Badge>
                                            {site.is_hidden && (
                                                <Badge variant="destructive">
                                                    Cachée
                                                </Badge>
                                            )}
                                        </div>
                                        <p className="text-sm text-muted-foreground break-all mb-2">{site.url}</p>
                                        <div className="flex flex-wrap gap-4 text-sm">
                                            <span>Intervalle: <strong>{site.scrape_interval_hours}h</strong></span>
                                            <span>Prochain scraping: <strong>{getNextScrapeTime(site)}</strong></span>
                                            {site.product_count !== undefined && (
                                                <span>Produits: <strong>{site.product_count}</strong></span>
                                            )}
                                        </div>
                                        {site.last_scraped_at && (
                                            <p className="text-xs text-muted-foreground mt-2">
                                                Dernier scraping: {new Date(site.last_scraped_at).toLocaleString('fr-FR')}
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex gap-2 ml-4">
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => scrapeMutation.mutate(site.id)}
                                            disabled={scrapeMutation.isPending}
                                        >
                                            <Play className="w-4 h-4" />
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => startEdit(site)}
                                        >
                                            <Edit className="w-4 h-4" />
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="destructive"
                                            onClick={() => {
                                                if (window.confirm(`Supprimer "${site.name}" ?`)) {
                                                    deleteSiteMutation.mutate(site.id);
                                                }
                                            }}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))
                )}
            </div>
        </div>
    );
}
